import { FC, ReactNode, useEffect } from 'react';
import { array, object, string } from 'yup';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

import { Box, Button, Form } from '@base-app';
import { CustomFormProps, EntryForm, InputType } from './CustomForm.types';
import { FormContainer, FormFieldStyled, StyledButtonContainer, StyledCustomContainer } from './CustomForm.styles';
import { GENETIC_COUNSELING_TRANSLATION } from '@/modules/genetic-counseling/constants';
import { useUserFieldsValidation } from '@/modules/shared/hooks/useUserFieldsValidation';

import CustomInput from './components/CustomInput/CustomInput';
import CustomSelect from './components/CustomSelect/CustomSelect';

const CustomForm: FC<CustomFormProps> = ({ data, isEditing, onSubmit }) => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const { email, phoneNumber } = useUserFieldsValidation();

  const schema = object().shape({
    dynamicInputs: array().of(
      object().shape({
        target: string()
          .when('required', (field, schema) => {
            return field ? schema.required(t('patient_profile.required')) : schema.notRequired();
          })
          .when('type', (field, schema) => {
            if (field === InputType.EMAIL) {
              return email;
            }

            if (field === InputType.PHONE) {
              return phoneNumber;
            }

            return schema;
          }),
      }),
    ),
  });

  const methods = useForm({
    values: {
      dynamicInputs: data,
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
  });

  const {
    control,
    formState: { isSubmitting, isValid, isDirty },
    handleSubmit,
    reset,
  } = methods;

  const { fields } = useFieldArray({
    control,
    name: 'dynamicInputs',
  });

  const handleFormSubmit: SubmitHandler<any> = (values): void => {
    onSubmit?.(values.dynamicInputs);
  };

  const getCustomItem = (field: EntryForm, index: number, isEditing: boolean): ReactNode => {
    switch (field.type) {
      case InputType.SELECT:
        return <CustomSelect index={index} field={field} isEditing={isEditing} />;

      default:
        return <CustomInput index={index} field={field} isEditing={isEditing} />;
    }
  };

  useEffect(() => {
    if (!isEditing) {
      reset();
    }
  }, [isEditing]);

  return (
    <FormProvider {...methods}>
      <Form css={{ margin: 0 }} data-testid={`custom-form`} onSubmit={handleSubmit(handleFormSubmit)}>
        <FormContainer>
          {fields?.map((field, index) => {
            return (
              <FormFieldStyled key={field.id}>
                <StyledCustomContainer data-testid={`custom-form-container-${index}`}>
                  <Box>{getCustomItem(field, index, !!isEditing)}</Box>
                </StyledCustomContainer>
              </FormFieldStyled>
            );
          })}
        </FormContainer>
        {isEditing && (
          <StyledButtonContainer>
            <Button data-testid={'custom-form-button'} disabled={!(!isSubmitting && isDirty && isValid)} type="submit">
              {t('patient_profile.save')}
            </Button>
          </StyledButtonContainer>
        )}
      </Form>
    </FormProvider>
  );
};

export default CustomForm;
